/**
 * MegaPokemonCard - Card component for Mega Evolution display in the main grid
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Zap } from 'lucide-react';
import { TypeBadgeGroup } from './TypeBadge';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../ui/skeleton';

export function MegaPokemonCard({ pokemon, index = 0 }) {
  const { isFavorite, toggleFavorite } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const pokemonId = pokemon.id;
  const favorite = isFavorite(pokemonId);
  
  // Use pre-calculated sprite URL from the data, or fallback
  const spriteUrl = pokemon.spriteUrl || `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
  
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(pokemonId);
  };
  
  // Format display name
  const displayName = pokemon.displayName || pokemon.name.replace(/-/g, ' ');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.02 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link 
        to={`/mega/${pokemon.id}`}
        data-testid={`pokemon-card-mega-${pokemonId}`}
        className="block"
      >
        <div className="relative bg-card rounded-2xl border-2 border-yellow-500/50 shadow-sm hover:shadow-xl hover:border-yellow-500 transition-all duration-300 overflow-hidden">
          {/* Mega indicator badge */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-violet-600/20 to-rose-500/20 py-1 px-3 flex items-center gap-1.5 z-10">
            <Zap className="w-3 h-3 text-yellow-500" />
            <span className="text-[10px] font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">MEGA</span>
          </div>
          
          {/* ID watermark */}
          <span className="absolute top-10 left-3 text-4xl font-black text-muted-foreground/10 select-none pointer-events-none">
            M{String(pokemon.baseId).padStart(3, '0')}
          </span>
          
          {/* Favorite button */}
          <button
            onClick={handleFavoriteClick}
            data-testid={`favorite-btn-mega-${pokemonId}`}
            className="absolute top-8 right-3 z-10 p-2 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform"
          >
            <Heart 
              className={`w-5 h-5 transition-colors ${
                favorite 
                  ? 'fill-rose-500 text-rose-500' 
                  : 'text-muted-foreground hover:text-rose-500'
              }`}
            />
          </button>
          
          {/* Pokemon image */}
          <div className="relative pt-10 pb-4 px-4 flex items-center justify-center h-44">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Skeleton className="w-32 h-32 rounded-full" />
              </div>
            )}
            <div className="relative">
              <img
                src={spriteUrl}
                alt={displayName}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  // Fallback to base Pokemon sprite if mega sprite fails
                  e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
                  setImageLoaded(true);
                }}
                className={`w-32 h-32 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
              {/* Mega glow effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/20 to-transparent rounded-full blur-xl -z-10" />
            </div>
          </div>
          
          {/* Pokemon info */}
          <div className="p-4 pt-0 space-y-2">
            <p className="text-xs font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-widest flex items-center gap-1">
              <Zap className="w-3 h-3" />
              #{String(pokemon.baseId).padStart(3, '0')}
            </p>
            <h3 className="text-lg font-bold capitalize text-foreground truncate">
              {displayName}
            </h3>
            <TypeBadgeGroup types={pokemon.types} size="xs" />
          </div>
          
          {/* Stats preview - BST */}
          <div className="px-4 pb-3">
            <div className="text-xs text-muted-foreground flex justify-between items-center">
              <span>BST</span>
              <span className="font-semibold text-violet-600 dark:text-violet-400">{pokemon.bst}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

// Get PokeAPI ID for mega forms
function getPokeApiId(apiName) {
  const megaIds = {
    'venusaur-mega': 10033,
    'charizard-mega-x': 10034,
    'charizard-mega-y': 10035,
    'blastoise-mega': 10036,
    'alakazam-mega': 10037,
    'gengar-mega': 10038,
    'kangaskhan-mega': 10039,
    'pinsir-mega': 10040,
    'gyarados-mega': 10041,
    'aerodactyl-mega': 10042,
    'mewtwo-mega-x': 10043,
    'mewtwo-mega-y': 10044,
    'ampharos-mega': 10045,
    'scizor-mega': 10046,
    'heracross-mega': 10047,
    'houndoom-mega': 10048,
    'tyranitar-mega': 10049,
    'blaziken-mega': 10050,
    'gardevoir-mega': 10051,
    'mawile-mega': 10052,
    'aggron-mega': 10053,
    'medicham-mega': 10054,
    'manectric-mega': 10055,
    'banette-mega': 10056,
    'absol-mega': 10057,
    'garchomp-mega': 10058,
    'lucario-mega': 10059,
    'abomasnow-mega': 10060,
    'beedrill-mega': 10090,
    'pidgeot-mega': 10073,
    'slowbro-mega': 10071,
    'steelix-mega': 10072,
    'sceptile-mega': 10065,
    'swampert-mega': 10064,
    'sableye-mega': 10066,
    'sharpedo-mega': 10070,
    'camerupt-mega': 10087,
    'altaria-mega': 10067,
    'glalie-mega': 10074,
    'salamence-mega': 10089,
    'metagross-mega': 10076,
    'latias-mega': 10062,
    'latios-mega': 10063,
    'lopunny-mega': 10088,
    'gallade-mega': 10068,
    'audino-mega': 10069,
    'diancie-mega': 10078,
    'rayquaza-mega': 10079
  };
  return megaIds[apiName] || null;
}

export function MegaPokemonCardSkeleton() {
  return (
    <div className="bg-card rounded-2xl border-2 border-yellow-500/30 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-violet-600/10 to-rose-500/10 py-1 px-3">
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="pt-8 pb-4 px-4 flex items-center justify-center h-44">
        <Skeleton className="w-32 h-32 rounded-full" />
      </div>
      <div className="p-4 pt-0 space-y-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-5 w-28" />
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default MegaPokemonCard;
